<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "@/components/PhotoBox.vue";
import { formatedDate2 } from "../../../utils/proposalValidation";

export default {
    name: "FPTemp5",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: ["proposal"],
    data() {
        return {
            footerImage: ""
        };
    },
    computed: {
        project() {
            return this.proposal.projects && this.proposal.projects.length > 0
                ? this.proposal.projects[0]
                : null;
        },
        headerColor() {
            return this.proposal && this.proposal.style && this.proposal.style.colorScheme && this.proposal.style.colorScheme.header;
        },
        bodyColor() {
            return this.proposal && this.proposal.style && this.proposal.style.colorScheme && this.proposal.style.colorScheme.body;
        },
        footerColor() {
            return this.proposal && this.proposal.style && this.proposal.style.colorScheme && this.proposal.style.colorScheme.footer;
        },
        nameLines() {
            const name = this.proposal.name || "";
            if (name.includes('\n')) return name.split('\n').slice(0, 2);

            const words = name.split(" ");
            if (words.length <= 1) return [name.trim(), ""];

            const mid = Math.ceil(words.length / 2);
            return [words.slice(0, mid).join(" ").trim(), words.slice(mid).join(" ").trim()];
        }
    },
    mounted() {
        this.generateGradients();
    },
    methods: {
        formatedDate2,
        generateGradients() {
            const fCanvas = document.createElement('canvas');
            fCanvas.width = 800;
            fCanvas.height = 400;
            const fCtx = fCanvas.getContext('2d');
            const fGrad = fCtx.createLinearGradient(0, 400, 0, 0);
            fGrad.addColorStop(0, 'rgba(0, 0, 0, 0.8)');
            fGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
            fCtx.fillStyle = fGrad;
            fCtx.fillRect(0, 0, 800, 400);
            this.footerImage = fCanvas.toDataURL();
        }
    }
};
</script>

<template>
    <div class="main-container" :style="{
        '--header-color': headerColor,
        '--body-color': bodyColor,
        '--footer-color': footerColor,
        '--heading-color': proposal.style.heading.color,
        '--heading-size': proposal.style.heading.size + 'px',
        '--heading-font': proposal.style.heading.font,
        '--heading-bold': proposal.style.heading.bold ? 700 : 400,
        '--heading-italic': proposal.style.heading.italic ? 'italic' : 'normal',
        '--heading-underline': proposal.style.heading.underline ? 'underline' : 'none',
        '--label-color': proposal.style.label.color,
        '--label-size': proposal.style.label.size + 'px',
        '--label-font': proposal.style.label.font,
        '--label-bold': proposal.style.label.bold ? 700 : 400,
        '--label-italic': proposal.style.label.italic ? 'italic' : 'normal',
        '--label-underline': proposal.style.label.underline ? 'underline' : 'none',
        '--text-color': proposal.style.text.color,
        '--text-size': proposal.style.text.size + 'px',
        '--text-font': proposal.style.text.font,
        '--text-bold': proposal.style.text.bold ? 700 : 400,
        '--text-italic': proposal.style.text.italic ? 'italic' : 'normal',
        '--text-underline': proposal.style.text.underline ? 'underline' : 'none',
    }">
        <div class="frontPage">
            <!-- Top Section -->
            <div class="top-sec">
                <div class="content-box">
                    <div class="proposal-name">
                        <h1 class="name-line-1">
                            <SpellCheckedText :value="nameLines[0] || ''"
                                @input="val => proposal.name = val + '\n' + (nameLines[1] || '')" />
                        </h1>
                        <h1 class="name-line-2">
                            <SpellCheckedText :value="nameLines[1] || ''"
                                @input="val => proposal.name = (nameLines[0] || '') + '\n' + val" />
                        </h1>
                    </div>

                    <div class="meta-info text-left">
                        <p class="location">
                            <SpellCheckedText :value="proposal.city || ''" @input="val => proposal.city = val" />
                            <span>{{ proposal.city ? ', ' : '' }}</span>
                            <SpellCheckedText :value="proposal.stateProvince || ''"
                                @input="val => proposal.stateProvince = val" />
                        </p>
                        <p class="date">
                            {{ formatedDate2(proposal.submissionDate) }}
                        </p>
                    </div>
                    <div class="logo-box photobox">
                        <PhotoBox :project="proposal" type="logo" bgColor="transparent" objectFit="contain" />
                    </div>
                </div>
            </div>

            <!-- Divider Line -->
            <div class="header-divider"></div>

            <!-- Bottom Section (Photo) -->
            <div class="photo-sec">
                <PhotoBox v-if="project" class="photobox" :project="project"
                    :bgColor="proposal.style.colorScheme && proposal.style.colorScheme.footer" />
                <div v-else class="image-placeholder" />

                <div v-if="project && footerImage" class="bottom-gradient">
                    <img :src="footerImage" class="w-full h-full" />
                </div>

                <div class="photo-captions">
                    <p class="caption-name" v-if="project">
                        <SpellCheckedText :value="project.projectName || ''"
                            @input="val => project.projectName = val" />
                    </p>
                    <p class="caption-location" v-if="project">
                        <SpellCheckedText :value="project.city || ''" @input="val => project.city = val" />
                        <span>{{ project.city ? ', ' : '' }}</span>
                        <SpellCheckedText :value="project.stateProvince || ''"
                            @input="val => project.stateProvince = val" />
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: block;
    width: 211mm;
    height: 296.83mm;
    min-height: 296.83mm;
    position: relative;
    box-sizing: border-box;
    background: white;
    overflow: hidden;

    &:hover {
        .photobox :deep(.photo-overlay) {
            opacity: 1;
        }

        .photobox :deep(img) {
            opacity: 0.9;
        }
    }

    .frontPage {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;

        .top-sec {
            background: var(--header-color);
            height: 48%;
            display: flex;
            align-items: flex-end;
            padding: 20px 60px 60px 60px;

            .content-box {
                width: 100%;
                display: flex;
                flex-direction: column;

                .proposal-name {
                    display: flex;
                    flex-direction: column;
                    position: relative;
                    margin-bottom: 20px;

                    .name-line-1,
                    .name-line-2 {
                        color: var(--heading-color);
                        font-size: var(--heading-size);
                        font-family: var(--heading-font);
                        font-weight: var(--heading-bold);
                        font-style: var(--heading-italic);
                        text-decoration: var(--heading-underline);
                        margin: 0;
                        width: fit-content;
                        line-height: 1;
                    }

                    .name-line-1 {
                        padding-right: 80px;
                        border-bottom: 2px solid var(--body-color);
                    }

                    .name-line-2 {
                        padding-right: 50px;
                        border-bottom: 3px solid var(--body-color);
                    }

                }

                .meta-info {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    text-transform: uppercase;

                    .location {
                        color: var(--label-color);
                        font-size: var(--label-size);
                        font-family: var(--label-font);
                        font-weight: var(--label-bold);
                        font-style: var(--label-italic);
                        text-decoration: var(--label-underline);
                        margin: 0;
                    }

                    .date {
                        color: var(--text-color);
                        font-weight: var(--text-bold);
                        font-style: var(--text-italic);
                        text-decoration: var(--text-underline);
                        font-family: var(--text-font);
                        font-size: var(--text-size);
                        margin: 0;
                    }
                }

                .logo-box {
                    margin-top: 30px;
                    width: 120px;
                    height: 50px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: var(--header-color);
                }
            }
        }

        .header-divider {
            height: 10px;
            background: var(--body-color);
            width: 100%;
        }

        .photo-sec {
            flex: 1;
            height: 50%;
            position: relative;
            background: #eee;

            .image-placeholder {
                width: 100%;
                height: 100%;
                background: var(--footer-color);
            }

            .bottom-gradient {
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 150px;
                z-index: 2;
                pointer-events: none;
            }

            .photo-captions {
                position: absolute;
                z-index: 50;
                bottom: 30px;
                left: 40px;
                color: rgba(255, 255, 255, 0.9);
                font-style: italic;
                font-size: 13px;
                line-height: 1.4;

                p {
                    margin: 0;
                }
            }
        }
    }
}
</style>
